import { useCallback } from 'react';
import { usePreference } from '@/lib/hooks/use-preference';

export interface KpiDefinition {
  id: string;
  label: string;
  accent: 'primary' | 'success' | 'warn' | 'danger';
  format: 'currency' | 'number' | 'percent';
  decimals: number;
  sparkData: number[];
  navTarget: string;
  category: string;
}


// ── Command Center KPI Registry (12 items) ──────────────────────────────────
// sparkData is empty until a history endpoint exists; MetricCard renders without it.
export const KPI_REGISTRY: KpiDefinition[] = [
  { id: 'revenue_collected', label: 'Revenue Collected', accent: 'primary', format: 'currency', decimals: 2, sparkData: [], navTarget: '/revenue', category: 'Revenue' },
  { id: 'pipeline_value', label: 'Pipeline Value', accent: 'success', format: 'currency', decimals: 1, sparkData: [], navTarget: '/pipeline', category: 'Revenue' },
  { id: 'evidence_forecast', label: 'Evidence Forecast', accent: 'success', format: 'currency', decimals: 1, sparkData: [], navTarget: '/forecast', category: 'Pipeline' },
  { id: 'deals_at_risk', label: 'Deals at Risk', accent: 'danger', format: 'number', decimals: 0, sparkData: [], navTarget: '/pipeline', category: 'Pipeline' },
  { id: 'followups_due', label: 'Follow-ups Due', accent: 'warn', format: 'number', decimals: 0, sparkData: [], navTarget: '/actions', category: 'Actions' },
  { id: 'projected_revenue', label: 'Projected Revenue', accent: 'primary', format: 'currency', decimals: 1, sparkData: [], navTarget: '/revenue', category: 'Revenue' },
  { id: 'revenue_goal', label: 'Revenue Goal', accent: 'success', format: 'currency', decimals: 1, sparkData: [], navTarget: '/revenue', category: 'Revenue' },
  { id: 'weekly_close_rate', label: 'Weekly Close Rate Needed', accent: 'warn', format: 'percent', decimals: 1, sparkData: [], navTarget: '/pipeline', category: 'Pipeline' },
  { id: 'active_clients', label: 'Active Clients', accent: 'primary', format: 'number', decimals: 0, sparkData: [], navTarget: '/clients', category: 'Clients' },
  { id: 'escalations', label: 'Pending Escalations', accent: 'danger', format: 'number', decimals: 0, sparkData: [], navTarget: '/actions', category: 'Actions' },
  { id: 'win_rate', label: 'Win Rate', accent: 'success', format: 'percent', decimals: 1, sparkData: [], navTarget: '/pipeline', category: 'Pipeline' },
  { id: 'total_deal_value', label: 'Total Deal Value', accent: 'primary', format: 'currency', decimals: 1, sparkData: [], navTarget: '/pipeline', category: 'Pipeline' },
];

// ── Finance Hub: Financial metrics ──────────────────────────────────────────
// Only metrics with a live source. Failed payments, net margin, churn rate and
// LTV:CAC come back when something computes them; a tile that always reads
// "Connect QuickBooks" while QuickBooks is connected is worse than no tile.
// Sources: AR / open invoices / 30d revenue / collection days ← the QuickBooks
// or Stripe summary; cash on hand / burn ← /v1/cash-runway (owner settings).
export const FINANCE_FINANCIAL_REGISTRY: KpiDefinition[] = [
  { id: 'accounts_receivable', label: 'Accounts Receivable', accent: 'warn', format: 'currency', decimals: 0, sparkData: [], navTarget: '/revenue', category: 'Billing' },
  { id: 'outstanding_invoices', label: 'Open Invoices', accent: 'warn', format: 'number', decimals: 0, sparkData: [], navTarget: '/revenue', category: 'Billing' },
  { id: 'mrr', label: 'Collected (30d)', accent: 'success', format: 'currency', decimals: 0, sparkData: [], navTarget: '/revenue', category: 'Billing' },
  { id: 'avg_collection_days', label: 'Avg Collection Days', accent: 'primary', format: 'number', decimals: 0, sparkData: [], navTarget: '/revenue', category: 'Billing' },
  { id: 'cash_on_hand', label: 'Cash on Hand', accent: 'success', format: 'currency', decimals: 0, sparkData: [], navTarget: '/revenue', category: 'Cash' },
  { id: 'monthly_burn_rate', label: 'Monthly Burn Rate', accent: 'primary', format: 'currency', decimals: 0, sparkData: [], navTarget: '/revenue', category: 'Cash' },
];

// ── Command Center KPI selection hook (DB-backed) ───────────────────────────
const DEFAULT_IDS = ['revenue_collected', 'pipeline_value', 'deals_at_risk', 'followups_due'];

export function useKpiSelection() {
  const [selectedIds, setSelectedIds] = usePreference<string[]>('kpi_selection', DEFAULT_IDS);

  const setSelection = useCallback((ids: string[]) => {
    setSelectedIds(ids);
  }, [setSelectedIds]);

  const selectedKpis = selectedIds
    .map(id => KPI_REGISTRY.find(k => k.id === id))
    .filter((k): k is KpiDefinition => k != null);

  return { selectedIds, selectedKpis, setSelection };
}

// ── Generic per-hub metric selection hook (DB-backed) ───────────────────────
export function useHubMetricSelection(
  prefKey: string,
  registry: KpiDefinition[],
  defaultIds: string[],
  _maxCount: number,
) {
  const [selectedIds, setSelectedIds] = usePreference<string[]>(prefKey, defaultIds);

  const setSelection = useCallback((ids: string[]) => {
    setSelectedIds(ids);
  }, [setSelectedIds]);

  const selectedKpis = selectedIds
    .map(id => registry.find(k => k.id === id))
    .filter((k): k is KpiDefinition => k != null);

  return { selectedIds, selectedKpis, setSelection, registry, maxCount: _maxCount, defaultIds };
}
